<template>
  <div>
    <h2 class="mb-4 text-lg">
      {{ image.collection && image.collection.name }}
    </h2>
    <!-- TITLE -->
    <!-- Important: keep {{ image.title }}<edit-in-place together without space to make sure there aren't any orphan -->
    <h1
      v-if="image.title"
      class="font-semibold mb-6 text-2xl text-gray-500"
    >
      {{ image.title }}<CorrectionsEdit
        class="editTooltip"
        :image-id="image.id"
        :enable="image.correction_enabled"
        :default-value="image.title"
        type="title"
        :label="$t('generic.editTitle')"
      />
    </h1>
    <h1
      v-if="!image.title && image.correction_enabled"
    >
      <CorrectionsEdit
        :image-id="image.id"
        :enable="image.correction_enabled"
        :default-value="image.title"
        type="title"
        :text-icon="$t('generic.addTitle')"
        :label="$t('generic.addTitle')"
      />
    </h1>
    <Date
      :date-min="image.date_shot_min"
      :date-max="image.date_shot_max"
      class="font-semibold py-1 text-gray-400 text-lg"
    />
    <!-- CAPTION -->
    <!-- Important: keep {{ image.caption }}<edit-in-place together without space to make sure there aren't any orphan -->
    <p
      v-if="image.caption"
      class="pb-3 pt-2 text-base text-gray-400"
    >
      {{ image.caption }}<CorrectionsEdit
        class="editTooltip"
        :image-id="image.id"
        :enable="image.correction_enabled"
        :default-value="image.caption"
        type="caption"
        :label="$t('generic.editCaption')"
      />
    </p>
    <p v-if="!image.caption && image.correction_enabled">
      <CorrectionsEdit
        :image-id="image.id"
        :enable="image.correction_enabled"
        :default-value="image.caption"
        type="caption"
        :text-icon="$t('generic.addCaption')"
        :label="$t('generic.addCaption')"
      />
    </p>

    <div class="metadata__group">
      <a
        v-if="image.download_link"
        id="label-download"
        :href="image.download_link"
        target="_blank"
        rel="noopener noreferrer"
        class="--no-style btn btn__metadata"
        download
      >
        <IconWrap
          class="mr-1"
          label="download"
        >
          <IconDownload />
        </IconWrap>
        {{ $t("pages.visitImage.download") }}
      </a>
      <a
        v-if="image.shop_link"
        id="label-shop"
        :href="image.shop_link"
        class="--no-style btn btn__metadata"
      >
        <IconWrap
          class="mr-1"
          label="shop"
        >
          <IconShop />
        </IconWrap>
        {{ $t("pages.visitImage.shop") }}
      </a>
      <button
        v-if="isNavigatorShareEnabled"
        id="label-share"
        class="btn btn__metadata"
        @click="sharePage"
      >
        <IconWrap
          class="mr-1"
          label="share"
        >
          <IconShare />
        </IconWrap>
        {{ $t("pages.visitImage.share") }}
      </button>
    </div>
    <!-- currentImage.title !== null prevent flash of button with the wrong colors -->
    <div
      v-if="currentImage.title !== null && currentImage.state !== 'waiting_validation' && modeContribute"
      class="flex flex-shrink-0 flex-wrap pt-5"
    >
      <!-- flex-shrink fix for safari -->
      <!-- Desktop -->
      <router-link
        v-if="isDesktop"
        v-tooltip.right="isCurrentImageLockedByOther && $t('pages.contribute.imageLocked')"
        :to="{ ...!isCurrentImageLockedByOther && geolocaliseRouteData(true) }"
        class="--no-style btn btn__fitMode"
        :target="isLoggedIn && isOwnerValidator ? '_blank' : '_self'"
        rel="noopener noreferrer"
        :disabled="isCurrentImageLockedByOther ? true : undefined"
        :aria-disabled="isCurrentImageLockedByOther ? true : undefined"
      >
        <template v-if="hasAttemptToGeoloc">
          {{ $t("pages.visitImage.newGeolocalisation") }}
        </template>
        <template v-else>
          {{ $t("pages.visitImage.contribute") }}
        </template>
      </router-link>
      <router-link
        v-if="isDesktop && hasAttemptToGeoloc"
        v-tooltip.right="isCurrentImageLockedByOther && $t('pages.contribute.imageLocked')"
        :to="{ ...!isCurrentImageLockedByOther && geolocaliseRouteData(false) }"
        class="--no-style btn btn__desktop btn__secondary"
        :target="isLoggedIn && isOwnerValidator ? '_blank' : '_self'"
        rel="noopener noreferrer"
        :disabled="isCurrentImageLockedByOther ? true : undefined"
        :aria-disabled="isCurrentImageLockedByOther ? true : undefined"
      >
        {{ $t("pages.visitImage.continueGeolocalisation") }}
      </router-link>
      <!-- Mobile Fake button with tooltip -->
      <router-link
        v-if="!isDesktop"
        v-tooltip.bottom="{ content: $t('generic.desktopOnly'), shown: false, triggers: [click, hover, focus] }"
        :to="''"
        class="--no-style btn btn__fitMode"
        disabled="true"
      >
        {{ $t("pages.visitImage.contribute") }}
      </router-link>
    </div>
    <div class="tabs">
      <button
        class="btn btn__metadata"
        :class="{'btn__metadata--active': !modeObservation, 'visit-mode': !modeContribute }"
        @click="setModeObservation(false)"
      >
        <IconWrap
          class="mr-1"
          label="annotate"
        >
          <IconInfo />
        </IconWrap>
        {{ $t('layout.metadataSlider.imageDetails') }}
      </button>
      <button
        v-if="image.observation_enabled"
        id="label-annotate"
        v-tooltip="toggleTooltipObsMode"
        class="btn btn__metadata"
        :class="{'btn__metadata--active': modeObservation, 'visit-mode': !modeContribute }"
        @click="setModeObservation(true)"
      >
        <IconWrap
          class="mr-1"
          label="annotate"
        >
          <IconComment />
        </IconWrap>
        {{ $tc('layout.observations.observation',2) }}
        <span
          v-if="image.nObs > 0"
          class="border border-gray-100 border-solid ml-1 px-2 rounded-lg text-gray-400"
        >
          <span class="sr-only">{{ $t('layout.observations.numberOf') }}</span>
          {{ image.nObs }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';

import CorrectionsEdit from '@/components/corrections/CorrectionsEdit';

import Date from '@/components/common/Date';
import { isDesktop } from '@/utils/style';

import IconComment from '@/assets/images/icons/Comment';
import IconDownload from '@/assets/images/icons/Download';
import IconShare from '@/assets/images/icons/Share';
import IconShop from '@/assets/images/icons/Shop';
import IconInfo from '@/assets/images/icons/Info';
import IconWrap from '@/assets/images/icons';

export default {
  name: 'ImageMetaPrimary',

  components: {
    Date,
    IconWrap,
    IconDownload,
    IconShare,
    IconComment,
    IconShop,
    IconInfo,
    CorrectionsEdit
  },

  props: {
    image: {
      type: Object,
      default: () => {}
    },

    modeVisit: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    toggleTooltipObsMode() {
      return this.modeObservation ? this.$t('layout.observations.disableMode') : this.$t('layout.observations.enableMode');
    },

    isNavigatorShareEnabled() {
      return navigator.share !== undefined;
    },

    ...mapState('app', ['modeObservation','modeContribute']),
    ...mapState('corrections', ['titleUpdate', 'captionUpdate']),
    ...mapState('explorer', ['currentImage', 'views']),
    ...mapGetters('explorer', ['isCurrentImageLockedByOther', 'hasAttemptToGeoloc']),
    ...mapGetters('user', ['isLoggedIn', 'isOwnerValidator'])
  },

  watch: {
    // call again the method if the route changes
    '$route': 'checkViews'
  },

  mounted() {
    this.checkViews();
  },

  methods: {
    checkViews() {
      const imageId = parseInt(this.$route.params.imageId, 10);

      // Increment view if is not in the cookie list
      if ((this.modeVisit && !this.views.visit.includes(imageId)) ||
        (!this.modeVisit && !this.views.contribute.includes(imageId))) {
        this.newView(imageId);
      }
    },

    newView(imageId) {
      if (this.modeVisit) {
        this.ADD_VIEWS_VISIT(imageId);
      } else {
        this.ADD_VIEWS_CONTRIBUTE(imageId);
      }
    },

    sharePage() {
      navigator.share({
        title: document.title,
        url: window.location.href
      });
    },

    geolocaliseRouteData(reset){
      let routeName = 'SelectMode';
      if (!this.currentImage.framing_mode || this.currentImage.framing_mode === 'single_image') {
        routeName = 'ProvideLocation';
      }
      return {
        name: routeName,
        params: { imageId: this.$route.params.imageId },
        // Force new geolocalisation (do not use data from last attempt)
        ...(reset ? { query: { reset: true } } : '' )
      };
    },

    isDesktop,

    ...mapMutations('explorer', ['ADD_VIEWS_VISIT', 'ADD_VIEWS_CONTRIBUTE']),
    ...mapActions('app', ['setModeObservation'])
  }
};
</script>

<style lang="postcss">
@media (hover: hover){
  .editTooltip button {
    @apply opacity-0;
  }
}

h1:hover .editTooltip button,
p:hover .editTooltip button,
.editTooltip:hover button,
.editTooltip button.focus-visible{
  @apply opacity-100;
}

.metadata__group, .tabs{
  @apply flex flex-wrap text-base pt-5;
}

.metadata__group > *, .tabs > *{
  margin-top:.5rem !important;
}

.tabs .btn__metadata--active {
  border-bottom: 3px solid;
  border-radius: 0px;
  border-color: theme("colors.orange.500");
  &:hover {
    border-color: black;
    color: theme("colors.orange.500");
    @apply text-gray-500 bg-gray-50;
  }
}

.tabs .btn__metadata--active.visit-mode {
  color: theme("colors.teal.500");
  border-color: theme("colors.teal.500");
  &:hover {
    border-color: black;
    color: theme("colors.teal.500");
    @apply text-gray-500 bg-gray-50;
  }
}

.btn.btn__metadata:hover::after, .btn.btn__metadata.btn__metadata--active::after {
  padding: 0;
}

</style>
